import React, { ReactElement, useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { NavLink, useParams } from "react-router-dom";
import { AppContext } from "AppContext";

import { isNil } from "ramda";

import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconSelector from "components/shared/images/IconSelector";

import { getIspList } from "redux/selectors/ispSelector";
import { getMailsList } from "redux/selectors/mailsSelector";

import ChangeMailStatus from "./actions/ChangeMailStatus";

const MailMonitoring = (): ReactElement => {
 const intl = useIntl();

 const ispList = useSelector(getIspList);
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("md"));

 const { socket } = useContext(AppContext);
 const params = useParams<{ mail_id: string; section: string }>();
 const maildetails = useSelector(getMailsList).find(
  (element) => element.id === Number(params.mail_id)
 );

 const [mailSize, setMailSize] = useState<string>("0 KB");

 const parseSizing = (value: number, type: string): string => {
  if (type && type.includes("M")) {
   return `${value} MB`;
  } else if (type && type.includes("G")) {
   return `${value} GB`;
  } else {
   return `${value} MB`;
  }
 };

 useEffect(() => {
  !isNil(socket) &&
   socket.on("maildomain_logs", async (value): Promise<void> => {
    for (let element of value) {
     if (element.domain === maildetails?.domain) {
      setMailSize(parseSizing(parseInt(element.mail_size), element.mail_size));
     }
    }
   });
  return () => {
   !isNil(socket) && socket.off("maildomain_logs");
  };
 }, [socket]);

 return (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack justifyContent="space-between" direction={desktopViewPort ? "row" : "column"}>
    <Stack spacing={1} alignItems="flex-start">
     <Typography fontWeight="bold" variant="unboundedTitle" fontSize={16} textTransform="uppercase">
      {maildetails?.domain}
     </Typography>
     <Stack direction="row" spacing={1}>
      <Tooltip title={intl.formatMessage({ id: "sites.details.goToServer" })}>
       <NavLink
        to={`/servers/manage/${ispList.find((element) => element.id === maildetails?.isp_id)
         ?.server_id}/specification`}
        style={{
         textDecoration: "none"
        }}>
        <Stack direction="row" spacing={1} sx={{ cursor: "pointer" }}>
         <IconSelector icon="ServerIcon" props={{ fontSize: "small", color: "kxOrange" }} />
         <Typography variant="subtitle2" color="kxOrange">
          {ispList.find((element) => element.id === maildetails?.isp_id)?.hostname}
         </Typography>
        </Stack>
       </NavLink>
      </Tooltip>
      <Typography>{ispList.find((element) => element.id === maildetails?.isp_id)?.ip1}</Typography>
     </Stack>
    </Stack>
   </Stack>
   <Stack direction="row" alignItems="center" mt={2} textAlign="left">
    <Typography variant="subtitle2" fontWeight="bold" minWidth={100}>
     <FormattedMessage id="sites.ssd" />
    </Typography>
    <Typography variant="subtitle2" textTransform="uppercase">
     {mailSize}
    </Typography>
   </Stack>
   <Divider sx={{ my: 1 }} />
   <Stack direction="row" justifyContent="flex-start">
    <ChangeMailStatus
     id={maildetails?.id || 0}
     isp_id={maildetails?.isp_id || 0}
     domain={maildetails?.domain || ""}
     status={maildetails?.active || false}
    />
   </Stack>
  </Paper>
 );
};

export default MailMonitoring;
